// /login — email + password against auth-svc, with TOTP MFA branches
// for platform-admin accounts (ADR-0024).
//
// State machine:
//   idle               — email + password form
//   totp-required      — code field (admin already enrolled)
//   totp-enrollment    — QR + code field (admin first login)

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api/error";
import { enrollTotp, login } from "../lib/auth/actions";
import { useSession } from "../lib/auth/useSession";

// `from` es el Location que guardó RequireAuth (o el destino del paso SSO
// fallido). El `search` es parte del destino: el CTA del correo al jefe es
// `/proyectos/<id>/consultas?consulta=<id>` (ADR-0145) y sin la query el
// jefe aterriza en la bandeja, no en el hilo.
type LocationState = { from?: { pathname?: string; search?: string }; ssoError?: boolean };

/** El destino tras iniciar sesión: ruta + query, o "/". */
export function loginDestination(state: LocationState | null): string {
  const from = state?.from;
  if (!from?.pathname) return "/";
  return from.pathname + (from.search ?? "");
}

type Phase =
  | { kind: "idle" }
  | { kind: "totp-required" }
  | { kind: "totp-enrollment"; qrSvg: string; provisioningUri: string };

export function Login() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as LocationState | null;
  const fromPath = loginDestination(locState);
  // Set when a PWA → dashboard handoff failed (link expired or already
  // used). Clear it from history so back-navigation doesn't re-show it.
  const ssoError = locState?.ssoError === true;
  useEffect(() => {
    if (ssoError) window.history.replaceState({}, "");
  }, [ssoError]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  useEffect(() => {
    if (session) navigate(fromPath, { replace: true });
  }, [session, fromPath, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (phase.kind === "totp-enrollment") {
        // Step 2 of first-login: post the code with email+password
        // re-auth so the server can bind the code to the secret.
        await enrollTotp({
          email: email.trim(),
          password,
          totpCode: totpCode.trim(),
        });
        navigate(fromPath, { replace: true });
        return;
      }
      const result = await login({
        email: email.trim(),
        password,
        ...(totpCode.trim() ? { totpCode: totpCode.trim() } : {}),
      });
      if (result.kind === "complete") {
        navigate(fromPath, { replace: true });
        return;
      }
      if (result.kind === "totp-required") {
        setPhase({ kind: "totp-required" });
        setTotpCode("");
        return;
      }
      // totp-enrollment
      setPhase({
        kind: "totp-enrollment",
        qrSvg: result.qrSvg,
        provisioningUri: result.provisioningUri,
      });
      setTotpCode("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "No pudimos iniciar sesión. Intenta de nuevo."
      );
    } finally {
      setBusy(false);
    }
  }

  const showPasswordFields = phase.kind === "idle";
  const showCodeField = phase.kind === "totp-required" || phase.kind === "totp-enrollment";
  const submitDisabled =
    busy || !email || !password || (showCodeField && totpCode.trim().length !== 6);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1 className="auth-card__title">
          {phase.kind === "totp-enrollment"
            ? "Configurar autenticador"
            : phase.kind === "totp-required"
              ? "Código de verificación"
              : "Iniciar sesión"}
        </h1>
        <p className="auth-card__sub">Dashboard · BitácoraPro</p>

        {phase.kind === "totp-enrollment" && (
          <div className="auth-card__totp">
            <p>
              Tu cuenta es <strong>platform admin</strong>. Necesitas configurar un autenticador de
              6 dígitos para continuar.
            </p>
            <ol>
              <li>Abre Google Authenticator, Authy, 1Password o tu app preferida.</li>
              <li>Escanea el código QR.</li>
              <li>
                Ingresa el código de 6 dígitos que aparece en la app para terminar el registro.
              </li>
            </ol>
            <div className="auth-card__qr" dangerouslySetInnerHTML={{ __html: phase.qrSvg }} />
            <details className="auth-card__totp-manual">
              <summary>No puedo escanear — entrada manual</summary>
              <code>{phase.provisioningUri}</code>
            </details>
          </div>
        )}

        {phase.kind === "totp-required" && (
          <p className="auth-card__sub">Ingresa el código de 6 dígitos de tu app autenticadora.</p>
        )}

        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}

        {ssoError && !error && (
          <p className="auth-error" role="alert">
            No pudimos conectarte automáticamente. Inicia sesión para continuar.
          </p>
        )}

        <form onSubmit={(e) => void onSubmit(e)} className="auth-form" noValidate>
          {showPasswordFields && (
            <>
              <div className="field">
                <label className="field__label" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  className="auth-input"
                  autoComplete="email"
                  placeholder="tu@empresa.cl"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="login-pass">
                  Contraseña
                </label>
                <input
                  id="login-pass"
                  type="password"
                  className="auth-input"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </>
          )}
          {showCodeField && (
            <div className="field">
              <label className="field__label" htmlFor="login-totp">
                Código (6 dígitos)
              </label>
              <input
                id="login-totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className="auth-input"
                autoComplete="one-time-code"
                placeholder="000000"
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
              />
            </div>
          )}
          <button type="submit" className="auth-submit" disabled={submitDisabled}>
            {busy
              ? "Procesando…"
              : phase.kind === "totp-enrollment"
                ? "Confirmar y entrar"
                : phase.kind === "totp-required"
                  ? "Verificar código"
                  : "Iniciar sesión"}
          </button>
        </form>
      </div>
    </main>
  );
}
